import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Empty, ErrorBanner, SeverityPill, formatUtc, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import { api } from '../../lib/api'
import { nextOffset, pageState, previousOffset } from '../explore/paging'

const REFRESH_MS = 5000

export function EventStreamPage() {
  const [minimumSeverity, setMinimumSeverity] = useState(0)
  const [live, setLive] = useState(true)
  const [offset, setOffset] = useState(0)
  const events = useAsync(
    () => api.listEvents({ minimumSeverity: minimumSeverity || undefined, offset }),
    [minimumSeverity, offset]
  )
  const { reload } = events

  // Polling rather than a websocket. The event stream is a screen an analyst leaves open, not a
  // trading terminal; a five-second poll costs one indexed query and removes a whole class of
  // connection-lifecycle bugs from a product that has more interesting problems to solve.
  // Paused automatically once the operator pages away from the newest events. A five-second refresh
  // under a moving offset scrolls the page out from under whoever is reading it, and the tail is only
  // meaningful at the top of the list anyway.
  const tailing = live && offset === 0

  useEffect(() => {
    if (!tailing) return
    const timer = window.setInterval(reload, REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [tailing, reload])

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Events</PageTitle>
          <p>What the fleet has sent back, normalized to OCSF.</p>
        </div>
        <div className="toolbar">
          <label htmlFor="severity" style={{ margin: 0 }}>
            Minimum severity
          </label>
          <select
            id="severity"
            style={{ width: 'auto' }}
            value={minimumSeverity}
            onChange={(event) => setMinimumSeverity(Number(event.target.value))}
          >
            <option value={0}>Everything</option>
            <option value={3}>Medium and above</option>
            <option value={4}>High and above</option>
            <option value={5}>Critical only</option>
          </select>
          <button type="button" onClick={() => setLive((current) => !current)} data-testid="toggle-live">
            {tailing ? 'Pause' : 'Resume'}
          </button>
          <button type="button" onClick={reload}>
            Refresh
          </button>
        </div>
      </div>

      <ErrorBanner message={events.error} />

      <div className="card">
        <div className="table-scroll">
          <table data-testid="event-table">
            <thead>
              <tr>
                <th>Occurred</th>
                <th className="secondary">Device</th>
                <th>Type</th>
                <th className="secondary">Class</th>
                <th>Severity</th>
                <th className="secondary">Plan</th>
                <th className="secondary">Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.value?.items.map((row) => (
                <tr key={row.eventRecordId} data-testid="event-row">
                  <td className="muted">
                    <Link to={`/events/${row.eventRecordId}`}>{formatUtc(row.occurredUtc)}</Link>
                  </td>
                  <td className="secondary">{row.deviceName}</td>
                  <td className="mono">{row.eventType}</td>
                  <td className="secondary">{row.className}</td>
                  <td>
                    <SeverityPill severityId={row.severityId} />
                  </td>
                  {/* An unresolved plan says so rather than rendering an empty cell.
                      This column used to be blank when no tier was read, on the grounds that absent is
                      not the free plan — which is right, and was then expressed as the one thing the
                      rule forbids. A blank is indistinguishable from a fact that is missing, so it
                      states the absence instead (D-263). Deliberately not a pill: it is the lack of a
                      plan, not a plan. */}
                  <td className="secondary" data-testid="event-plan">
                    {row.accountTier ? (
                      <span className="pill sev-4">{row.accountTier}</span>
                    ) : (
                      <span className="muted" title="No policy asked for the account, or the row could not be read. This is not the free plan.">
                        not read
                      </span>
                    )}
                  </td>
                  <td className="secondary">
                    {row.message}
                    {row.matchedRuleNames && (
                      <div className="muted">
                        {row.matchCount} match{row.matchCount === 1 ? '' : 'es'} · {row.matchedRuleNames}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {events.value && events.value.items.length === 0 && (
          <Empty>
            {events.loading
              ? 'Loading…'
              : 'No events yet. Enroll a device and publish a policy that publishes its result.'}
          </Empty>
        )}

        {/* The list used to end here, at fifty rows, with no count and nothing after it. An operator
            arriving from a tile reading "6,426 Events, 14 days" had no way to see that 6,376 of them
            were not on the screen, and no way to reach them (D-275). */}
        {events.value && events.value.items.length > 0 && (
          <div className="card-body toolbar">
            <span data-testid="event-summary">
              {pageState(events.value.total, offset, events.value.items.length).summary}
              {tailing ? ' Refreshing every five seconds.' : ''}
            </span>
            <div className="toolbar" style={{ marginLeft: 'auto' }} data-testid="event-paging">
              <button
                type="button"
                onClick={() => setOffset(previousOffset(offset, 50))}
                disabled={offset === 0}
                data-testid="event-previous"
              >
                Newer
              </button>
              <button
                type="button"
                onClick={() => setOffset(nextOffset(events.value!.total, offset, 50))}
                disabled={!pageState(events.value.total, offset, events.value.items.length).canGoForward}
                data-testid="event-next"
              >
                Older
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
